import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Database, Plus, Upload, Trash2, Edit3, X, Search, Globe, Users, User, ChevronRight, ChevronDown, Share2, RefreshCw, Tags, History, CheckSquare } from "lucide-react";
import { toast } from "sonner";
import ClientPicker from "@/components/ClientPicker";
import { ContextChips } from "@/components/datalake/ContextChips";
import { ValidityChip } from "@/components/datalake/ValidityChip";
import { ContextSelect } from "@/components/datalake/ContextSelect";
import { ValidityFields, appendMeta } from "@/components/datalake/ValidityFields";
import { MetadataModal } from "@/components/datalake/MetadataModal";
import { BulkMetadataModal } from "@/components/datalake/BulkMetadataModal";
import { VersionTimelineModal } from "@/components/datalake/VersionTimeline";
import { useContextVocab, VALIDITY_META } from "@/components/datalake/vocab";

const KIND_SUGGESTIONS = ["subject", "visit", "labresult", "document", "adverse_event", "concomitant_medication", "consent", "lab_manual", "protocol", "custom"];

const SCOPE_META = {
    common: {
        label: "COMMON",
        chipTone: "border-emerald-300/40 bg-emerald-300/10 text-emerald-300",
        title: "Common Knowledge",
        sub: "Accessible to all AI Agents",
        Icon: Globe,
    },
    multi_client_shared: {
        label: "SHARED",
        chipTone: "border-[#7C3AED]/50 bg-[#7C3AED]/15 text-[#c7a6ff]",
        title: "Shared Resource",
        sub: "Referenced by Multiple Clients",
        Icon: Users,
    },
    client_owned: {
        label: "SINGLE TENANT",
        chipTone: "border-amber-300/40 bg-amber-300/5 text-amber-200",
        title: "Owned by 1 Client",
        sub: "Direct Ownership",
        Icon: User,
    },
    unowned: {
        label: "UNCLAIMED",
        chipTone: "border-white/15 bg-white/5 text-white/55",
        title: "Unclaimed",
        sub: "Legacy / un-tagged entity",
        Icon: Database,
    },
};

function tagTone(tag) {
    if (tag === "COMMON") return "border-emerald-300/40 bg-emerald-300/10 text-emerald-300";
    if (tag.startsWith("CLIENT_")) return "border-[#7C3AED]/40 bg-[#7C3AED]/10 text-[#c7a6ff]";
    return "border-white/10 bg-white/5 text-white/65";
}

export default function DataLakePage() {
    const [tab, setTab] = useState("items"); // items | documents
    const [items, setItems] = useState([]);
    const [facets, setFacets] = useState({ companies: [], sites: [], kinds: [] });
    const [counts, setCounts] = useState({ common: 0, shared: 0, single: 0 });
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        ownership_scope: "",
        client_id: "",
        kind: "",
        external_ref: "",
        q: "",
        context: new URLSearchParams(window.location.search).get("context") || "", // deep link from the Context Usage Map
        validity: "",
        hide_expired: false,
    });
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [docOpen, setDocOpen] = useState(false);
    const [shareTarget, setShareTarget] = useState(null);
    const [metaTarget, setMetaTarget] = useState(null);
    const [versionsTarget, setVersionsTarget] = useState(null);
    const [selected, setSelected] = useState(() => new Set());
    const [bulkOpen, setBulkOpen] = useState(false);
    const [expanded, setExpanded] = useState(() => new Set());
    const { byId: ctxById, active: ctxActive, irbRelevant } = useContextVocab();

    // Global big-data search — uses the `/admin/datalake/search` endpoint
    // (text index `datalake_text_search`). When `searchResults` is set
    // the list view collapses to the score-sorted hit set; clearing
    // the input restores the regular browse view.
    const [searchQuery, setSearchQuery] = useState("");
    const [searchTypeFilter, setSearchTypeFilter] = useState("");
    const [searchResults, setSearchResults] = useState(null);
    const [searching, setSearching] = useState(false);

    const runSearchWith = async (q, type, hideExpired) => {
        const term = (q ?? searchQuery).trim();
        if (!term) {
            setSearchResults(null);
            return;
        }
        setSearching(true);
        try {
            const params = new URLSearchParams({ q: term, limit: "50" });
            const ft = type ?? searchTypeFilter;
            if (ft) params.set("file_type", ft);
            if (filters.context) params.set("context", filters.context);
            if (filters.validity) params.set("validity", filters.validity);
            const hide = hideExpired ?? filters.hide_expired;
            if (!hide && !filters.validity) params.set("include_expired", "true");
            const { data } = await api.get(`/admin/datalake/search?${params.toString()}`);
            setSearchResults(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
            setSearchResults(null);
        } finally {
            setSearching(false);
        }
    };
    const runSearch = (q, type) => runSearchWith(q, type, undefined);

    const clearSearch = () => {
        setSearchQuery("");
        setSearchTypeFilter("");
        setSearchResults(null);
    };

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            Object.entries(filters).forEach(([k, v]) => k !== "hide_expired" && v && params.set(k, v));
            if (filters.hide_expired) params.set("include_expired", "false");
            if (tab === "documents") params.set("kind", "document");
            params.set("limit", "200");
            const { data } = await api.get(`/admin/datalake/items?${params.toString()}`);
            setItems(data.items || []);
            setFacets(data.facets || { companies: [], sites: [], kinds: [] });
            setCounts(data.ownership_counts || { common: 0, shared: 0, single: 0 });
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); setSelected(new Set()); }, [tab, filters.ownership_scope, filters.context, filters.validity, filters.hide_expired]); // eslint-disable-line react-hooks/exhaustive-deps
    const toggleSelect = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

    const removeItem = async (it) => {
        const reason = prompt(`Reason for deleting ${it.id.slice(0, 8)}…?`);
        if (!reason || !reason.trim()) return;
        try {
            await api.delete(`/admin/datalake/items/${it.id}?reason=${encodeURIComponent(reason)}`);
            toast.success("Deleted");
            load();
        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    };

    const reindex = async () => {
        // Privileged maintenance — installs text+compound indexes and
        // backfills `content` / `file_hash` / `tags` on legacy ingest
        // mirror rows from `protocols` / `ctas` / `budgets` /
        // `study_builds`. Idempotent — rerunning costs nothing.
        if (!window.confirm(
            "Rebuild big-data indexes and backfill content on legacy ingest rows?\n\n" +
            "This walks every Protocol / CTA / Budget / Study Build mirror that's " +
            "missing canonical `content` and re-populates it from the source row. " +
            "Idempotent — safe to run anytime."
        )) return;
        const t = toast.loading("Reindexing datalake…");
        try {
            const { data } = await api.post(`/admin/datalake/_reindex`);
            toast.dismiss(t);
            toast.success(`Reindex done · ${data.backfilled} rows backfilled · ${data.indexes_installed ? "indexes refreshed" : "indexes unchanged"}`);
            load();
        } catch (e) {
            toast.dismiss(t);
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
    };

    const toggleExpand = (id) => {
        setExpanded((p) => {
            const next = new Set(p);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const filteredByQuery = useMemo(() => {
        if (!filters.q) return items;
        const ql = filters.q.toLowerCase();
        return items.filter((it) =>
            (it.document_name || "").toLowerCase().includes(ql)
            || (it.id || "").toLowerCase().includes(ql)
            || (it.external_ref || "").toLowerCase().includes(ql)
            || (it.tags || []).some((t) => t.toLowerCase().includes(ql)),
        );
    }, [items, filters.q]);

    return (
        <div className="px-8 py-8" data-testid="datalake-page">
            <div className="flex items-start justify-between mb-8">
                <div>
                    <div className="label-xs text-[#7C3AED]">Data Platform</div>
                    <h1 className="font-display text-4xl tracking-tight mt-2">Data Lake</h1>
                    <p className="text-sm text-white/50 mt-2 max-w-2xl">
                        Multi-tenant document store with relationship-based ownership.
                        Every entity carries one of three classifications:
                        <span className="text-emerald-300 font-mono"> common</span>,
                        <span className="text-[#c7a6ff] font-mono"> multi_client_shared</span>, or
                        <span className="text-amber-200 font-mono"> client_owned</span> — enforced at every retrieval layer.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={reindex} data-testid="dl-reindex"
                        title="Rebuild big-data indexes and backfill content/file_hash/tags on legacy ingest mirror rows"
                        className="border border-white/15 text-white/75 px-3 py-2 rounded-md text-xs hover:bg-white/5 flex items-center gap-2">
                        <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.8} /> Reindex
                    </button>
                    {tab === "items" ? (
                        <button onClick={() => { setEditing(null); setOpen(true); }} data-testid="dl-new"
                            className="bg-[#7C3AED] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#7C3AED]/90 flex items-center gap-2">
                            <Plus className="w-4 h-4" strokeWidth={1.8} /> New item
                        </button>
                    ) : (
                        <>
                            <button onClick={() => setDocOpen("icf")} data-testid="dl-upload-icf"
                                className="border border-rose-300/40 text-rose-200 px-4 py-2 rounded-md text-sm font-medium hover:bg-rose-300/10 flex items-center gap-2">
                                <Upload className="w-4 h-4" strokeWidth={1.8} /> Upload ICF
                            </button>
                            <button onClick={() => setDocOpen("document")} data-testid="dl-upload"
                                className="bg-[#7C3AED] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#7C3AED]/90 flex items-center gap-2">
                                <Upload className="w-4 h-4" strokeWidth={1.8} /> Upload document
                            </button>
                        </>
                    )}
                </div>
            </div>

            <div className="flex border-b border-white/10 mb-4">
                <Tab active={tab === "items"} onClick={() => setTab("items")} testId="tab-items" label="Items" />
                <Tab active={tab === "documents"} onClick={() => setTab("documents")} testId="tab-documents" label="Documents" />
            </div>

            {/* Global big-data search — runs Mongo $text against the
                `datalake_text_search` index installed by
                `ensure_datalake_indexes`. Tenant-scoped server-side.
                When `searchResults` is non-null, the regular browse
                list below is hidden and replaced by the score-sorted
                results panel. */}
            <div className="bg-gradient-to-b from-[#0a0a0a] to-[#070707] border border-white/10 rounded-md p-4 mb-4">
                <div className="flex items-center gap-3">
                    <div className="flex-1 relative">
                        <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                            type="search"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") runSearch(); }}
                            placeholder='Search the whole datalake — e.g. "Otsuka SOL-ADHD", site contract clause text, protocol number…'
                            data-testid="dl-global-search-input"
                            className="w-full bg-[#050505] border border-white/10 rounded-md pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#7C3AED]/60"
                        />
                    </div>
                    <button
                        onClick={() => runSearch()}
                        disabled={searching || !searchQuery.trim()}
                        data-testid="dl-global-search-btn"
                        className="bg-[#7C3AED] text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-[#7C3AED]/90 disabled:opacity-40"
                    >
                        {searching ? "Searching…" : "Search"}
                    </button>
                    {searchResults && (
                        <button
                            onClick={clearSearch}
                            data-testid="dl-global-search-clear"
                            className="border border-white/15 text-white/75 px-3 py-2.5 rounded-md text-xs hover:bg-white/5"
                        >
                            Clear
                        </button>
                    )}
                </div>
                {/* Type chips — drive the file_type filter for the next search. */}
                <div className="flex items-center gap-1.5 flex-wrap mt-3">
                    <span className="text-[10px] uppercase tracking-wider text-white/40 mr-1">Filter:</span>
                    {[
                        ["", "all"],
                        ["cta", "CTAs"],
                        ["budget", "budgets"],
                        ["protocol", "protocols"],
                        ["common_knowledge", "common knowledge"],
                        ["agent_output", "AI outputs"],
                        ["study_build", "study builds"],
                    ].map(([val, label]) => (
                        <button
                            key={val || "all"}
                            onClick={() => {
                                setSearchTypeFilter(val);
                                if (searchQuery.trim()) runSearch(searchQuery, val);
                            }}
                            data-testid={`dl-search-type-${val || "all"}`}
                            className={`px-2.5 py-1 rounded-full text-[11px] font-mono border transition-colors ${
                                searchTypeFilter === val
                                    ? "border-[#7C3AED]/60 bg-[#7C3AED]/15 text-white"
                                    : "border-white/10 text-white/55 hover:bg-white/5"
                            }`}
                        >
                            {label}
                            {searchResults?.buckets?.[val] != null && val !== "" && (
                                <span className="ml-1.5 text-white/40">·{searchResults.buckets[val]}</span>
                            )}
                        </button>
                    ))}
                    <label className="ml-3 flex items-center gap-1.5 text-[11px] text-white/60 cursor-pointer" data-testid="dl-search-hide-expired-label">
                        <input type="checkbox" checked={filters.hide_expired} data-testid="dl-search-hide-expired" className="accent-[#7C3AED]"
                            onChange={(e) => {
                                const v = e.target.checked;
                                setFilters((p) => ({ ...p, hide_expired: v }));
                                if (searchQuery.trim()) setTimeout(() => runSearchWith(searchQuery, searchTypeFilter, v), 0);
                            }} />
                        hide expired / not-yet-valid
                    </label>
                </div>
                {searchResults && (
                    <div className="mt-3 text-[11px] text-white/55 font-mono">
                        <span className="text-[#c7a6ff]">{searchResults.total}</span> result{searchResults.total === 1 ? "" : "s"} for
                        <span className="text-white/85"> "{searchResults.q}"</span>
                        {Object.keys(searchResults.buckets).length > 0 && (
                            <span className="ml-2 text-white/40">
                                · {Object.entries(searchResults.buckets).map(([k, v]) => `${v} ${k}`).join(" · ")}
                            </span>
                        )}
                    </div>
                )}
            </div>

            {searchResults ? (
                <SearchResultsPanel results={searchResults} ctxById={ctxById} />
            ) : (
              <>
                {/* Ownership scope filter cards */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                <ScopeCard
                    testId="dl-scope-common"
                    active={filters.ownership_scope === "common"}
                    onClick={() => setFilters((p) => ({ ...p, ownership_scope: p.ownership_scope === "common" ? "" : "common" }))}
                    Icon={Globe} title="Common Knowledge" sub="Accessible to all tenants" count={counts.common} tone="text-emerald-300"
                />
                <ScopeCard
                    testId="dl-scope-shared"
                    active={filters.ownership_scope === "shared"}
                    onClick={() => setFilters((p) => ({ ...p, ownership_scope: p.ownership_scope === "shared" ? "" : "shared" }))}
                    Icon={Users} title="Shared Across Clients" sub="Multi-tenant relationship" count={counts.shared} tone="text-[#c7a6ff]"
                />
                <ScopeCard
                    testId="dl-scope-single"
                    active={filters.ownership_scope === "single"}
                    onClick={() => setFilters((p) => ({ ...p, ownership_scope: p.ownership_scope === "single" ? "" : "single" }))}
                    Icon={User} title="Single Tenant" sub="One client only" count={counts.single} tone="text-amber-200"
                />
            </div>

            {/* Search + filter form */}
            <form onSubmit={(e) => { e.preventDefault(); load(); }} className="bg-[#0a0a0a] border border-white/10 rounded-md p-4 mb-4 grid grid-cols-5 gap-3" data-testid="dl-filters">
                <Input label="Search (name / id / tag)" value={filters.q} on={(v) => setFilters((p) => ({ ...p, q: v }))} testId="dl-q" />
                <FacetSelect label="Filter by ClientID" value={filters.client_id} on={(v) => setFilters((p) => ({ ...p, client_id: v }))} options={facets.companies} testId="dl-client" />
                {tab === "items" && (
                    <FacetSelect label="Entity type" value={filters.kind} on={(v) => setFilters((p) => ({ ...p, kind: v }))} options={facets.kinds} testId="dl-kind" />
                )}
                <Input label="External ref" value={filters.external_ref} on={(v) => setFilters((p) => ({ ...p, external_ref: v }))} testId="dl-extref" />
                <div className="flex items-end">
                    <button type="submit" className="w-full px-4 py-2 bg-[#7C3AED] text-white rounded-md text-sm font-medium hover:bg-[#7C3AED]/90 flex items-center justify-center gap-1.5">
                        <Search className="w-3.5 h-3.5" strokeWidth={1.8} /> Apply
                    </button>
                </div>
                <div className="col-span-5 flex flex-wrap items-end gap-3 pt-1 border-t border-white/5" data-testid="dl-context-filters">
                    <div>
                        <div className="label-xs mb-1.5">Context</div>
                        <select value={filters.context} onChange={(e) => setFilters((p) => ({ ...p, context: e.target.value }))} data-testid="dl-filter-context"
                            className="bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono min-w-[200px]">
                            <option value="">All contexts</option>
                            {Object.entries(facets.contexts || {}).sort().map(([id, n]) => (
                                <option key={id} value={id}>{`${ctxById[id]?.label || id} · ${n}`}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <div className="label-xs mb-1.5">Validity</div>
                        <select value={filters.validity} onChange={(e) => setFilters((p) => ({ ...p, validity: e.target.value }))} data-testid="dl-filter-validity"
                            className="bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono min-w-[180px]">
                            <option value="">Any validity</option>
                            {Object.entries(VALIDITY_META).map(([id, v]) => (
                                <option key={id} value={id}>{v.label}{facets.validity?.[id] != null ? ` · ${facets.validity[id]}` : ""}</option>
                            ))}
                        </select>
                    </div>
                    <label className="flex items-center gap-2 text-xs text-white/70 pb-2.5 cursor-pointer" data-testid="dl-filter-hide-expired-label">
                        <input type="checkbox" checked={filters.hide_expired} onChange={(e) => setFilters((p) => ({ ...p, hide_expired: e.target.checked }))} data-testid="dl-filter-hide-expired" className="accent-[#7C3AED]" />
                        Hide expired / not-yet-valid
                    </label>
                    <div className="text-[11px] text-white/40 pb-2.5 ml-auto font-mono" data-testid="dl-validity-summary">
                        {facets.validity ? `${facets.validity.expiring_soon || 0} expiring soon · ${facets.validity.expired || 0} expired · ${facets.validity.not_yet_valid || 0} not yet valid` : ""}
                    </div>
                </div>
            </form>

            {selected.size > 0 && (
                <div className="sticky top-2 z-20 mb-3 flex items-center gap-3 rounded-md border border-[#7C3AED]/50 bg-[#120a24]/95 backdrop-blur px-4 py-2.5" data-testid="dl-bulk-bar">
                    <CheckSquare className="w-4 h-4 text-[#c7a6ff]" />
                    <span className="text-sm text-white/90 font-mono" data-testid="dl-bulk-count">{selected.size} selected</span>
                    <button onClick={() => setBulkOpen(true)} data-testid="dl-bulk-edit" className="inline-flex items-center gap-1.5 rounded bg-[#7C3AED] px-3 py-1.5 text-xs text-white hover:bg-[#7C3AED]/90"><Tags className="w-3.5 h-3.5" /> Edit contexts &amp; validity</button>
                    <button onClick={() => setSelected(new Set(filteredByQuery.map((i) => i.id)))} data-testid="dl-bulk-select-all" className="text-xs text-white/60 hover:text-white">Select all {filteredByQuery.length} visible</button>
                    <button onClick={() => setSelected(new Set())} data-testid="dl-bulk-clear" className="ml-auto text-xs text-white/60 hover:text-white">Clear selection</button>
                </div>
            )}

            <div className="bg-[#0a0a0a] border border-white/10 rounded-md overflow-hidden">
                <table className="w-full">
                    <thead>
                        <tr className="text-left">
                            <th className="label-xs px-3 py-3 w-8">
                                <input type="checkbox" aria-label="Select all visible" data-testid="dl-select-all"
                                    checked={filteredByQuery.length > 0 && filteredByQuery.every((i) => selected.has(i.id))}
                                    onChange={(e) => setSelected(e.target.checked ? new Set(filteredByQuery.map((i) => i.id)) : new Set())} className="accent-[#7C3AED]" />
                            </th>
                            <th className="label-xs px-2 py-3 w-6"></th>
                            <th className="label-xs px-5 py-3">Document Name</th>
                            <th className="label-xs px-5 py-3">Unique Document ID</th>
                            <th className="label-xs px-5 py-3">Kind</th>
                            <th className="label-xs px-5 py-3">Ownership</th>
                            <th className="label-xs px-5 py-3">Context · Tags</th>
                            <th className="label-xs px-5 py-3">Updated</th>
                            <th className="label-xs px-5 py-3 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={9} className="px-5 py-8 text-center text-xs text-white/40 font-mono">Loading…</td></tr>
                        ) : filteredByQuery.length === 0 ? (
                            <tr><td colSpan={9} className="px-5 py-16 text-center">
                                <Database className="w-10 h-10 text-white/20 mx-auto mb-3" strokeWidth={1.2} />
                                <div className="text-sm text-white/60">No items match the filter.</div>
                            </td></tr>
                        ) : filteredByQuery.map((it) => {
                            const meta = SCOPE_META[it.classification] || SCOPE_META.unowned;
                            const isExpanded = expanded.has(it.id);
                            const ScopeIcon = meta.Icon;
                            return (
                                <React.Fragment key={it.id}>
                                    <tr className={`data-row border-t border-white/5 ${selected.has(it.id) ? "bg-[#7C3AED]/5" : ""}`} data-testid={`dl-row-${it.id}`}>
                                        <td className="px-3 py-3 align-top">
                                            <input type="checkbox" checked={selected.has(it.id)} onChange={() => toggleSelect(it.id)} aria-label="Select document" data-testid={`dl-select-${it.id}`} className="accent-[#7C3AED] mt-1" />
                                        </td>
                                        <td className="px-2 py-3 align-top">
                                            <button onClick={() => toggleExpand(it.id)} data-testid={`dl-expand-${it.id}`}
                                                className="p-1 rounded hover:bg-white/10 text-white/50">
                                                {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                                            </button>
                                        </td>
                                        <td className="px-5 py-3 max-w-[260px]">
                                            <div className="text-sm text-white/90 truncate" title={it.document_name} data-testid={`dl-doc-name-${it.id}`}>{it.document_name}</div>
                                            <div className="text-[11px] text-white/45 font-mono truncate">
                                                {it.payload?.size_bytes ? `${(it.payload.size_bytes / 1024).toFixed(1)} KB · ` : ""}
                                                {it.payload?.extraction_method || it.payload?.mime || "—"}
                                            </div>
                                        </td>
                                        <td className="px-5 py-3 font-mono text-[11px] text-white/65 max-w-[180px] truncate" data-testid={`dl-doc-id-${it.id}`} title={it.id}>{it.id}</td>
                                        <td className="px-5 py-3">
                                            <span className="px-1.5 py-0.5 rounded-sm border border-[#7C3AED]/30 bg-[#7C3AED]/5 text-[11px] font-mono text-[#7C3AED]">{it.kind}</span>
                                        </td>
                                        <td className="px-5 py-3" title={`${meta.title} — ${meta.sub}`}>
                                            <div className="flex items-center gap-2">
                                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider border rounded-sm ${meta.chipTone}`}
                                                    data-testid={`dl-scope-chip-${it.id}`}>
                                                    <ScopeIcon className="w-3 h-3" strokeWidth={2} /> {meta.label}
                                                </span>
                                                <span className="text-[11px] font-mono text-white/55">
                                                    {it.classification === "common"
                                                        ? "Common Knowledge"
                                                        : it.client_count > 1
                                                            ? `Shared Across ${it.client_count} Clients`
                                                            : "Owned by 1 Client"}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-5 py-3">
                                            <div className="flex flex-col gap-1 max-w-[280px]">
                                                <div className="flex flex-wrap items-center gap-1">
                                                    {(it.contexts?.length || it.validity_state) && <ValidityChip state={it.validity_state} meta={it.doc_meta} itemId={it.id} />}
                                                    <ContextChips contexts={it.contexts} byId={ctxById} itemId={it.id} />
                                                </div>
                                                <div className="flex flex-wrap gap-1">
                                                    {(it.tags || []).filter((t) => !t.startsWith("context:")).slice(0, 4).map((t, i) => (
                                                        <span key={`${t}-${i}`} className={`px-1.5 py-0.5 rounded-sm border text-[10px] font-mono ${tagTone(t)}`}>{t}</span>
                                                    ))}
                                                    {(it.tags || []).length > 4 && (
                                                        <span className="text-[10px] font-mono text-white/40">+{(it.tags || []).length - 4}</span>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-5 py-3 font-mono text-xs text-white/60 whitespace-nowrap">{new Date(it.updated_at).toLocaleString()}</td>
                                        <td className="px-5 py-3">
                                            <div className="flex items-center justify-end gap-1.5">
                                                <button onClick={() => setMetaTarget(it)} title="Contexts & validity" data-testid={`dl-metadata-${it.id}`} className="p-1.5 rounded hover:bg-white/10 text-white/70">
                                                    <Tags className="w-3.5 h-3.5" strokeWidth={1.6} />
                                                </button>
                                                <button onClick={() => setVersionsTarget(it)} title="Version timeline" data-testid={`dl-versions-${it.id}`} className="p-1.5 rounded hover:bg-white/10 text-white/70">
                                                    <History className="w-3.5 h-3.5" strokeWidth={1.6} />
                                                </button>
                                                <button onClick={() => setShareTarget(it)} title="Share with another client" data-testid={`dl-share-${it.id}`} className="p-1.5 rounded hover:bg-[#7C3AED]/20 text-[#c7a6ff]">
                                                    <Share2 className="w-3.5 h-3.5" strokeWidth={1.6} />
                                                </button>
                                                {tab === "items" && (
                                                    <button onClick={() => { setEditing(it); setOpen(true); }} className="p-1.5 rounded hover:bg-white/10 text-white/70" data-testid={`dl-edit-${it.id}`}>
                                                        <Edit3 className="w-3.5 h-3.5" strokeWidth={1.6} />
                                                    </button>
                                                )}
                                                <button onClick={() => removeItem(it)} className="p-1.5 rounded hover:bg-red-500/20 text-white/70 hover:text-red-400" data-testid={`dl-delete-${it.id}`}>
                                                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.6} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                    {isExpanded && (
                                        <tr className="bg-[#050505]/60 border-b border-white/5" data-testid={`dl-expand-row-${it.id}`}>
                                            <td colSpan={9} className="px-5 py-4">
                                                <OwnershipDetailsPanel item={it} meta={meta} />
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
              </>
            )}

            {open && <ItemModal item={editing} onClose={() => { setOpen(false); setEditing(null); }} onSaved={() => { setOpen(false); setEditing(null); load(); }} />}
            {docOpen && <DocumentModal onClose={() => setDocOpen(false)} onSaved={() => { setDocOpen(false); load(); }} ctxOptions={ctxActive} irbRelevant={irbRelevant} initialKind={docOpen === "icf" ? "icf" : "document"} />}
            {shareTarget && <ShareModal item={shareTarget} onClose={() => setShareTarget(null)} onDone={() => { setShareTarget(null); load(); }} />}
            {metaTarget && <MetadataModal item={metaTarget} onClose={() => setMetaTarget(null)} onSaved={() => { setMetaTarget(null); load(); }} />}
            {versionsTarget && <VersionTimelineModal item={versionsTarget} onClose={() => setVersionsTarget(null)} onChanged={load} />}
            {bulkOpen && <BulkMetadataModal items={items.filter((i) => selected.has(i.id))} onClose={() => setBulkOpen(false)} onDone={() => { setBulkOpen(false); setSelected(new Set()); load(); }} />}
        </div>
    );
}

function SearchResultsPanel({ results, ctxById }) {
    // Render the score-sorted hits returned by /admin/datalake/search.
    // Highlighting strategy: we already get a server-side snippet
    // centered on the first matched term — render it verbatim so the
    // operator sees the match context without scrolling into the
    // expanded row. Click "Open" to expand for full payload.
    const items = results.items || [];
    if (!items.length) {
        return (
            <div className="bg-[#0a0a0a] border border-white/10 rounded-md p-12 text-center" data-testid="dl-search-empty">
                <Search className="w-10 h-10 text-white/15 mx-auto mb-3" strokeWidth={1.2} />
                <div className="text-sm text-white/60">No matches for "{results.q}".</div>
                {results.hidden_by_validity > 0 ? (
                    <div className="mt-3 inline-block bg-amber-500/10 border border-amber-500/40 text-amber-100 px-4 py-2 rounded-md text-[12px]" data-testid="dl-search-hidden-by-validity">
                        {results.hidden_by_validity} matching document{results.hidden_by_validity === 1 ? " is" : "s are"} expired or not yet valid and hidden — untick "hide expired" to include them.
                    </div>
                ) : (
                    <div className="text-[11px] text-white/40 mt-1 font-mono">
                        Tip: try quoting a phrase ("sponsor agreement") or removing a too-narrow type filter.
                    </div>
                )}
                {results.diagnostics?.needs_reindex && (
                    <div className="mt-4 inline-block max-w-xl bg-amber-500/10 border border-amber-500/40 text-amber-100 px-4 py-3 rounded-md text-[12px] text-left"
                        data-testid="dl-search-needs-reindex">
                        <strong className="text-amber-300">Reindex required.</strong> {results.diagnostics.hint}
                    </div>
                )}
            </div>
        );
    }
    return (
        <div className="space-y-2" data-testid="dl-search-results">
            {items.map((it) => {
                const ft = it.file_type || it.source_kind || "other";
                const score = typeof it.score === "number" ? it.score.toFixed(2) : null;
                return (
                    <div key={it.id} className="bg-[#0a0a0a] border border-white/10 rounded-md p-4 hover:border-white/25 transition-colors"
                        data-testid={`dl-search-hit-${it.id}`}>
                        <div className="flex items-start justify-between gap-3 mb-1.5">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="px-1.5 py-0.5 text-[10px] uppercase tracking-wider font-mono border border-[#7C3AED]/40 bg-[#7C3AED]/10 text-[#c7a6ff] rounded-sm">
                                        {ft}
                                    </span>
                                    {it.sponsor && (
                                        <span className="text-[11px] text-amber-200 font-mono">sponsor:{it.sponsor}</span>
                                    )}
                                    {it.protocol_name && (
                                        <span className="text-[11px] text-emerald-300 font-mono">protocol:{it.protocol_name}</span>
                                    )}
                                    {it.validity_state && <ValidityChip state={it.validity_state} meta={it.doc_meta} itemId={`hit-${it.id}`} />}
                                    <ContextChips contexts={it.contexts} byId={ctxById} itemId={`hit-${it.id}`} />
                                    {score && (
                                        <span className="ml-auto text-[10px] text-white/35 font-mono">score {score}</span>
                                    )}
                                </div>
                                <div className="text-sm text-white/90 truncate" title={it.title}>{it.title || it.kind || it.id}</div>
                                {it.summary && (
                                    <div className="text-[11px] text-white/50 truncate mt-0.5">{it.summary}</div>
                                )}
                            </div>
                        </div>
                        {it.snippet && (
                            <pre className="text-[11px] font-mono text-white/65 bg-[#050505] border border-white/5 rounded p-2 mt-2 whitespace-pre-wrap max-h-32 overflow-auto"
                                data-testid={`dl-search-snippet-${it.id}`}>
                                {it.snippet}
                            </pre>
                        )}
                        <div className="flex items-center gap-3 mt-2 text-[10px] text-white/40 font-mono">
                            {it.file_hash && <span>hash:{it.file_hash.slice(0, 16)}…</span>}
                            <span>id:{it.id.slice(0, 8)}…</span>
                            {it.updated_at && <span>· {new Date(it.updated_at).toLocaleDateString()}</span>}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}


function ScopeCard({ testId, active, onClick, Icon, title, sub, count, tone }) {
    return (
        <button
            onClick={onClick}
            data-testid={testId}
            className={`bg-[#0a0a0a] border rounded-md p-4 text-left transition-colors ${active ? "border-white/40" : "border-white/10 hover:bg-white/5"}`}
        >
            <div className="flex items-center gap-2">
                <Icon className={`w-4 h-4 ${tone}`} strokeWidth={1.6} />
                <span className="label-xs">{title}</span>
            </div>
            <div className="font-display text-2xl mt-1">{count}</div>
            <div className="text-[11px] text-white/45 mt-0.5">{sub}</div>
        </button>
    );
}

function OwnershipDetailsPanel({ item, meta }) {
    const owners = item.owners || [];
    const lastUpdated = item.updated_at ? new Date(item.updated_at).toLocaleString() : "—";
    // Big-data retention surface: the retained content + payload + identity fields
    // — populated by the May-2026 datalake-retention fix. Legacy rows without
    // these fields fall back to the older preview rendering below.
    const content = item.content || item?.preview?.rendered_text || "";
    const contentBytes = content ? new Blob([content]).size : 0;
    const payloadJson = item.payload
        ? (typeof item.payload === "string" ? item.payload : JSON.stringify(item.payload, null, 2))
        : "";
    const identity = {
        file_hash: item.file_hash,
        file_type: item.file_type,
        sponsor: item.sponsor,
        protocol_name: item.protocol_name,
        protocol_id: item.protocol_id,
    };
    const hasIdentity = Object.values(identity).some(Boolean);
    return (
        <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
                <div>
                    <div className="label-xs mb-2">Classification</div>
                    <div className="text-sm text-white/85">{meta.title}</div>
                    <div className="text-[11px] text-white/45 mt-0.5">{meta.sub}</div>
                    <div className="text-[11px] text-white/55 font-mono mt-2">
                        relationship: <span className="text-white/80">{item.relationship || "—"}</span>
                    </div>
                </div>
                <div>
                    <div className="label-xs mb-2">Linked Clients ({owners.length})</div>
                    {owners.length === 0 ? (
                        <div className="text-[11px] text-white/40 font-mono">No client owners — common-knowledge entity</div>
                    ) : (
                        <div className="flex flex-wrap gap-1">
                            {owners.map((cid) => (
                                <span key={cid} className="px-1.5 py-0.5 border border-[#7C3AED]/40 bg-[#7C3AED]/10 text-[#c7a6ff] text-[10px] font-mono rounded-sm">
                                    CLIENT_{cid}
                                </span>
                            ))}
                        </div>
                    )}
                    {item.companions?.length > 0 && (
                        <div className="text-[11px] text-white/45 font-mono mt-2">
                            Primary owner: <span className="text-white/80">{item.company_id}</span>
                        </div>
                    )}
                </div>
                <div>
                    <div className="label-xs mb-2">Metadata</div>
                    <div className="text-[11px] font-mono text-white/55 space-y-0.5">
                        <div>kind: <span className="text-white/80">{item.kind}</span></div>
                        <div>source_system_id: <span className="text-white/80">{item.source_system_id || item.source_kind || "—"}</span></div>
                        <div>external_ref: <span className="text-white/80">{item.external_ref || item.source_id || "—"}</span></div>
                        <div>last_updated: <span className="text-white/80">{lastUpdated}</span></div>
                    </div>
                </div>
            </div>

            {hasIdentity && (
                <div className="border-t border-white/10 pt-3">
                    <div className="label-xs mb-2">Reuse identity</div>
                    <div className="grid grid-cols-5 gap-3 text-[11px] font-mono text-white/65">
                        <div><span className="text-white/40">file_type:</span> <span className="text-white/85">{identity.file_type || "—"}</span></div>
                        <div className="col-span-2"><span className="text-white/40">file_hash:</span> <span className="text-white/85" title={identity.file_hash}>{identity.file_hash ? `${identity.file_hash.slice(0, 16)}…` : "—"}</span></div>
                        <div><span className="text-white/40">sponsor:</span> <span className="text-white/85">{identity.sponsor || "—"}</span></div>
                        <div><span className="text-white/40">protocol:</span> <span className="text-white/85">{identity.protocol_name || "—"}</span></div>
                    </div>
                </div>
            )}

            {content && (
                <div className="border-t border-white/10 pt-3">
                    <div className="flex items-center justify-between mb-2">
                        <div className="label-xs">Retained content</div>
                        <div className="text-[10px] text-white/40 font-mono">
                            {(contentBytes / 1024).toFixed(1)} KB · full-text indexed
                        </div>
                    </div>
                    <pre className="max-h-72 overflow-auto text-[11px] font-mono text-white/75 bg-[#050505] border border-white/10 rounded p-3 whitespace-pre-wrap" data-testid={`dl-content-${item.id}`}>
                        {content.slice(0, 12000)}
                        {content.length > 12000 && "\n…[truncated for display — full content is in the indexed row]"}
                    </pre>
                </div>
            )}

            {payloadJson && payloadJson !== "{}" && (
                <details className="border-t border-white/10 pt-3">
                    <summary className="label-xs cursor-pointer text-white/70 hover:text-white">Structured payload</summary>
                    <pre className="mt-2 max-h-72 overflow-auto text-[11px] font-mono text-white/65 bg-[#050505] border border-white/10 rounded p-3" data-testid={`dl-payload-${item.id}`}>
                        {payloadJson.slice(0, 12000)}
                        {payloadJson.length > 12000 && "\n…[truncated]"}
                    </pre>
                </details>
            )}
        </div>
    );
}

function ShareModal({ item, onClose, onDone }) {
    const [clientId, setClientId] = useState("");
    const [siteId, setSiteId] = useState("");
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);
    const owners = item.owners || [];

    const add = async () => {
        if (!clientId.trim()) {
            toast.error("client_id required");
            return;
        }
        setSaving(true);
        try {
            await api.post(`/admin/datalake_items/${item.id}/owners/add`, {
                client_id: clientId.trim(),
                site_id: siteId.trim() || null,
                reason: reason || null,
            });
            toast.success(`Shared with ${clientId.trim()}`);
            onDone();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (cid) => {
        if (!window.confirm(`Remove CLIENT_${cid} from this entity?`)) return;
        try {
            const { data } = await api.post(`/admin/datalake_items/${item.id}/owners/remove`, {
                client_id: cid, reason: "manual unshare via UI",
            });
            toast.success(data.deleted ? "Last owner removed — orphan deleted" : `Unshared from ${cid}`);
            onDone();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
    };

    return (
        <Modal title={`Share · ${item.document_name?.slice(0, 40) || item.id.slice(0, 8)}`} onClose={onClose} testId="dl-share-modal">
            <div className="space-y-4">
                <div className="border border-white/10 rounded-md p-3">
                    <div className="label-xs mb-2">Current owners</div>
                    {owners.length === 0 ? (
                        <div className="text-[11px] text-white/40 font-mono">Common-knowledge entity (no client owners)</div>
                    ) : (
                        <div className="flex flex-wrap gap-1.5">
                            {owners.map((cid) => (
                                <span key={cid} className="inline-flex items-center gap-1.5 px-2 py-0.5 border border-[#7C3AED]/40 bg-[#7C3AED]/10 text-[#c7a6ff] text-[11px] font-mono rounded-sm">
                                    CLIENT_{cid}
                                    <button onClick={() => remove(cid)} data-testid={`dl-share-remove-${cid}`} className="text-red-300 hover:text-red-200">
                                        <X className="w-3 h-3" />
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}
                </div>
                <div className="border border-white/10 rounded-md p-3 space-y-2">
                    <div className="label-xs">Add a client</div>
                    <ClientPicker
                        value={clientId}
                        onChange={setClientId}
                        label="Client"
                        testId="dl-share-client"
                        excludeDisabled
                    />
                    <Input label="Site ID (optional)" value={siteId} on={setSiteId} testId="dl-share-site" />
                    <Input label="Reason (audit)" value={reason} on={setReason} testId="dl-share-reason" />
                    <div className="flex justify-end pt-1">
                        <button onClick={add} disabled={saving} data-testid="dl-share-add"
                            className="px-4 py-2 text-sm bg-[#7C3AED] text-white rounded-md hover:bg-[#7C3AED]/90 disabled:opacity-40">
                            {saving ? "Sharing…" : "Share"}
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    );
}

function ItemModal({ item, onClose, onSaved }) {
    const editing = !!item;
    const [companyId, setCompanyId] = useState(item?.company_id || "");
    const [siteId, setSiteId] = useState(item?.site_id || "");
    const [kind, setKind] = useState(item?.kind || "subject");
    const [sourceSystemId, setSourceSystemId] = useState(item?.source_system_id || "");
    const [externalRef, setExternalRef] = useState(item?.external_ref || "");
    const [tags, setTags] = useState((item?.tags || []).join(", "));
    const [payload, setPayload] = useState(JSON.stringify(item?.payload || {}, null, 2));
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        let parsed;
        try { parsed = JSON.parse(payload || "{}"); } catch (err) { toast.error("Payload must be valid JSON"); return; }
        setLoading(true);
        const tagList = tags.split(",").map((t) => t.trim()).filter(Boolean);
        try {
            if (editing) {
                await api.patch(`/admin/datalake/items/${item.id}`, {
                    kind, source_system_id: sourceSystemId || null, external_ref: externalRef || null,
                    payload: parsed, tags: tagList,
                });
                toast.success("Updated");
            } else {
                await api.post("/admin/datalake/items", {
                    company_id: companyId, site_id: siteId, kind,
                    source_system_id: sourceSystemId || null, external_ref: externalRef || null,
                    payload: parsed, tags: tagList,
                });
                toast.success("Created");
            }
            onSaved();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal title={editing ? `Edit item · ${item.id.slice(0, 8)}` : "New data lake item"} onClose={onClose} testId="dl-item-modal">
            <form onSubmit={submit} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                    <Input label="Company ID" required value={companyId} on={setCompanyId} disabled={editing} testId="dl-form-company" />
                    <Input label="Site ID" required value={siteId} on={setSiteId} disabled={editing} testId="dl-form-site" />
                </div>
                <div>
                    <div className="label-xs mb-1.5">Kind</div>
                    <input list="kind-suggestions" value={kind} onChange={(e) => setKind(e.target.value)} data-testid="dl-form-kind"
                        className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono" />
                    <datalist id="kind-suggestions">
                        {KIND_SUGGESTIONS.map((k) => <option key={k} value={k} />)}
                    </datalist>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <Input label="Source system" value={sourceSystemId} on={setSourceSystemId} placeholder="edc.medidata" testId="dl-form-system"
                        hint="Stable id for the third-party system" />
                    <Input label="External reference" value={externalRef} on={setExternalRef} placeholder="S-0001" testId="dl-form-extref"
                        hint="The id used inside that system" />
                </div>
                <Input label="Tags (comma-separated)" value={tags} on={setTags} testId="dl-form-tags" />
                <div>
                    <div className="label-xs mb-1.5">Payload (JSON)</div>
                    <textarea rows={8} value={payload} onChange={(e) => setPayload(e.target.value)} data-testid="dl-form-payload"
                        className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-xs font-mono focus:outline-none focus:border-white/30" />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-white/15 rounded-md hover:bg-white/5">Cancel</button>
                    <button type="submit" disabled={loading} data-testid="dl-form-submit" className="px-4 py-2 bg-[#7C3AED] text-white rounded-md text-sm font-medium hover:bg-[#7C3AED]/90 disabled:opacity-40">
                        {loading ? "Saving…" : (editing ? "Update" : "Create")}
                    </button>
                </div>
            </form>
        </Modal>
    );
}

function DocumentModal({ onClose, onSaved, ctxOptions, irbRelevant, initialKind = "document" }) {
    const [companyId, setCompanyId] = useState("");
    const [siteId, setSiteId] = useState("");
    const [sourceSystemId, setSourceSystemId] = useState("");
    const [externalRef, setExternalRef] = useState("");
    const [tags, setTags] = useState("");
    const [docKind, setDocKind] = useState(initialKind);
    const [contexts, setContexts] = useState(initialKind === "icf" ? ["icf"] : []);
    const [meta, setMeta] = useState({});
    const [title, setTitle] = useState("");
    const [protocolId, setProtocolId] = useState("");
    const fileRef = useRef();
    const [loading, setLoading] = useState(false);
    const isIcf = docKind === "icf";
    const pdfOnly = isIcf || contexts.includes("icf");
    const showIrb = irbRelevant ? irbRelevant(contexts) : true;

    const switchKind = (k) => {
        setDocKind(k);
        setContexts(k === "icf" ? ["icf"] : contexts.filter((c) => c !== "icf"));
        if (k !== "icf") setMeta((m) => ({ ...m, irb_approved: null, irb_approval_date: "", irb_name: "" }));
    };

    const submit = async (e) => {
        e.preventDefault();
        const file = fileRef.current.files?.[0];
        if (!file) { toast.error("Select a file"); return; }
        if (pdfOnly && !file.name.toLowerCase().endsWith(".pdf")) { toast.error("Informed consent forms must be PDF files"); return; }
        setLoading(true);
        const fd = new FormData();
        fd.append("file", file);
        fd.append("company_id", companyId);
        fd.append("site_id", siteId);
        fd.append("kind", docKind);
        if (title) fd.append("title", title);
        if (protocolId) fd.append("protocol_id", protocolId);
        if (sourceSystemId) fd.append("source_system_id", sourceSystemId);
        if (externalRef) fd.append("external_ref", externalRef);
        if (tags) fd.append("tags", tags);
        contexts.forEach((c) => fd.append("contexts", c));
        appendMeta(fd, showIrb ? meta : { ...meta, irb_approved: null, irb_approval_date: "", irb_name: "" });
        try {
            const { data } = await api.post("/admin/datalake/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(data?._dedup ? "Identical document already present — scope tagged" : `${isIcf ? "ICF" : "Document"} ingested · ${(data?.validity_state || "valid").replace("_", " ")}`);
            onSaved();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal title={isIcf ? "Upload informed consent form" : "Upload document"} onClose={onClose} testId="dl-doc-modal" wide>
            <form onSubmit={submit} className="space-y-3">
                <div className="flex items-center gap-1.5" data-testid="dl-doc-kind">
                    {[["document", "Site document", "any PDF / DOCX / XLSX / image with one or more contexts"], ["icf", "Informed consent (ICF)", "PDF only · IRB approval, consent version and validity window"]].map(([k, l, d]) => (
                        <button key={k} type="button" onClick={() => switchKind(k)} title={d} data-testid={`dl-doc-kind-${k}`} aria-pressed={docKind === k}
                            className={`px-2.5 py-1 rounded-sm border text-[11px] font-mono ${docKind === k ? "border-[#7C3AED]/60 bg-[#7C3AED]/15 text-[#c7a6ff]" : "border-white/10 text-white/50 hover:bg-white/5"}`}>{l}</button>
                    ))}
                    <span className="text-[11px] text-white/40 ml-2">{isIcf ? "Regulatory document — separate from budgets / CTAs (financial, no IRB fields)." : "Budgets and CTAs have their own ingests under Study assets."}</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <Input label="Company ID" required value={companyId} on={setCompanyId} testId="dl-doc-company" />
                    <Input label="Site ID" required value={siteId} on={setSiteId} testId="dl-doc-site" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <Input label={isIcf ? "Consent title" : "Title"} value={title} on={setTitle} placeholder={isIcf ? "Main ICF — adult cohort" : "defaults to the filename"} testId="dl-doc-title" />
                    {isIcf ? <Input label="Protocol ID" value={protocolId} on={setProtocolId} placeholder="protocol this consent belongs to" testId="dl-doc-protocol" />
                        : <Input label="Tags" value={tags} on={setTags} testId="dl-doc-tags" />}
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <Input label="Source system" value={sourceSystemId} on={setSourceSystemId} testId="dl-doc-system" />
                    <Input label="External ref" value={externalRef} on={setExternalRef} testId="dl-doc-extref" />
                </div>
                <ContextSelect value={contexts} onChange={setContexts} options={ctxOptions || []} locked={isIcf ? ["icf"] : []} testId="dl-doc-contexts" />
                <ValidityFields value={meta} onChange={setMeta} testId="dl-doc-validity" showIrb={showIrb}
                    irbHint={isIcf ? undefined : "IRB approval fields appear once an IRB-relevant context (ICF, protocol, regulatory, safety) is selected."} />
                <div>
                    <div className="label-xs mb-1.5">File * {pdfOnly && <span className="normal-case tracking-normal text-white/40">(PDF only)</span>}</div>
                    <input type="file" ref={fileRef} required accept={pdfOnly ? ".pdf,application/pdf" : undefined} data-testid="dl-doc-file"
                        className="block w-full text-xs file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-[#7C3AED] file:text-white file:cursor-pointer" />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-white/15 rounded-md hover:bg-white/5">Cancel</button>
                    <button type="submit" disabled={loading} data-testid="dl-doc-submit" className="px-4 py-2 bg-[#7C3AED] text-white rounded-md text-sm font-medium hover:bg-[#7C3AED]/90 disabled:opacity-40">
                        {loading ? "Uploading…" : (isIcf ? "Upload ICF" : "Upload")}
                    </button>
                </div>
            </form>
        </Modal>
    );
}

function Tab({ active, onClick, testId, label }) {
    return (
        <button onClick={onClick} data-testid={testId}
            className={`px-5 py-3 text-xs font-mono tracking-wide uppercase ${active ? "text-white border-b-2 border-[#7C3AED]" : "text-white/50 hover:text-white/80"}`}>
            {label}
        </button>
    );
}

function Input({ label, value, on, placeholder, required, hint, testId, disabled }) {
    return (
        <div>
            <div className="label-xs mb-1.5">{label}{required && <span className="text-[#7C3AED] ml-1">*</span>}</div>
            <input value={value} required={required} disabled={disabled} placeholder={placeholder} onChange={(e) => on(e.target.value)} data-testid={testId}
                className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:border-white/30 disabled:opacity-50" />
            {hint && <div className="text-[11px] text-white/40 mt-1">{hint}</div>}
        </div>
    );
}

function FacetSelect({ label, value, on, options, testId }) {
    return (
        <div>
            <div className="label-xs mb-1.5">{label}</div>
            <select value={value} onChange={(e) => on(e.target.value)} data-testid={testId}
                className="bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono min-w-[160px]">
                <option value="">All</option>
                {options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
        </div>
    );
}

function Modal({ title, children, onClose, testId, wide }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onMouseDown={(e)=>{e.currentTarget._dn=e.target===e.currentTarget;}} onMouseUp={(e)=>{if(e.currentTarget._dn&&e.target===e.currentTarget)onClose();e.currentTarget._dn=false;}}>
            <div className={`w-full ${wide ? "max-w-2xl" : "max-w-lg"} max-h-[90vh] overflow-y-auto bg-[#0a0a0a] border border-white/15 rounded-md`} onClick={(e) => e.stopPropagation()} data-testid={testId}>
                <div className="px-5 py-4 border-b border-white/10 flex items-center sticky top-0 bg-[#0a0a0a] z-10">
                    <div className="font-display text-lg tracking-tight">{title}</div>
                    <button onClick={onClose} className="ml-auto text-white/60 hover:text-white"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-5">{children}</div>
            </div>
        </div>
    );
}
